import { Component, Inject, Renderer2, OnInit } from "@angular/core";
import { DataService, DialogLogoutComponent, FG_APP_CONSTANTS, FgAppConstants, LocalStorageService, UserService } from "fg-library";
import { LayoutService } from "../shared/components/main-nav/layoutservice";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface Theme {
  label: string;
  class: string;
  primaryColor: string;
  accentColor: string;
  warnColor: string;
}

@Component({
    selector: "app-settings-panel",
    templateUrl: "./settings-panel.component.html",
    styleUrls: ["./settings-panel.component.scss"],
    standalone: false
})
export class SettingsPanelComponent implements OnInit {

  currentUserDetails: any;
  config: any;
  AppPreference: any = {};
  theme = "fingress-dark-theme";
  direction: "ltr" | "rtl" = "ltr";
  currentLayout = "beryllium";
  currentTheme: string = "fingress-dark-theme";
  localConfig: any = {};
  mockDataEnabled = false;

  themes: Theme[] = [
    { label: "Fingress Red", class: "fingress-red-theme", primaryColor: "#d00a0d", accentColor: "#2962ff", warnColor: "#E53935" },
    { label: "Fingress Blue", class: "fingress-blue-theme", primaryColor: "#2962ff", accentColor: "#d00a0d", warnColor: "#2962ff" },
    { label: "Fingress Green", class: "fingress-green-theme", primaryColor: "#03A9F4", accentColor: "#d00a0d", warnColor: "#E53935" },
    { label: "Fingress Orange", class: "fingress-orange-theme", primaryColor: "#03A9F4", accentColor: "#4CAF50", warnColor: "#E53935" },
    { label: "Dark", class: "dark-theme", primaryColor: "#424242", accentColor: "#FF6F00", warnColor: "#F44336" },
    { label: "Trade Portal", class: "tradeportal-theme", primaryColor: "#0059ab", accentColor: "#f7951e", warnColor: "#d32f2f" },
    { label: "Light Green", class: "fingress-light-green-theme", primaryColor: "#087f82", accentColor: "#152133", warnColor: "#d32f2f" },
  ];

  constructor(
    private localStorage: LocalStorageService,
    private renderer: Renderer2,
    private layoutService: LayoutService,
    public dataService: DataService,
    public router: Router,
    public dialog: MatDialog,
    public userService: UserService,
    @Inject(FG_APP_CONSTANTS) public fgAppConstant: FgAppConstants,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Load AppPreference & Direction safely
      const appPreferenceStr = this.localStorage.get("AppPreference");
      const appDirectionStr = this.localStorage.get("appDirection");

      let appPreference = appPreferenceStr ? JSON.parse(appPreferenceStr) : null;
      let appDirection = (appDirectionStr as "ltr" | "rtl") || "ltr";

      if (!appPreference) appPreference = { theme: 'tradeportal-theme', layout: 'HORIZONTAL' };

      this.AppPreference = appPreference;
      this.currentLayout = appPreference.layout;
      this.currentTheme = appPreference.theme;
      this.direction = appDirection;

      this.setTheme(appPreference.theme);
      this.setDirection(appDirection);
    }

    this.userService.currentUser.subscribe((response) => {
      this.currentUserDetails = response;
      if (this.currentUserDetails?.lastLoginTime)
        this.currentUserDetails["lastLoginTime"] = new Date(this.currentUserDetails["lastLoginTime"]);
    });
  }

  setTheme(themeClass: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.themes.forEach(t => this.renderer.removeClass(document.body, t.class));
      this.renderer.addClass(document.body, themeClass);
      this.localStorage.set("AppPreference", JSON.stringify({ ...this.AppPreference, theme: themeClass }));
    }

    this.currentTheme = themeClass;
    this.AppPreference.theme = themeClass;
    this.localConfig.currentLogo = (themeClass === "dark-theme" || themeClass === "tradeportal-theme")
      ? "assets/img/logo_white.png"
      : "assets/img/logo.png";
  }

  setDirection(dir: "ltr" | "rtl") {
    this.direction = dir;
    if (isPlatformBrowser(this.platformId)) {
      document.body.dir = dir;
      this.localStorage.set("appDirection", dir);
    }
  }

  setLayout(layout: string) {
    this.currentLayout = layout;
    this.layoutService.setLayout(layout);
    this.AppPreference.layout = layout;
    if (isPlatformBrowser(this.platformId)) {
      this.localStorage.set("AppPreference", JSON.stringify(this.AppPreference));
    }
  }

  toggleMockData(enabled: boolean): void {
    this.mockDataEnabled = enabled;
    this.layoutService.setMocData(this.mockDataEnabled);
    if (isPlatformBrowser(this.platformId)) {
      this.localStorage.set('mockDataEnabled', enabled);
    }
  }

  logout(directLogin: boolean = false) {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('appLanguage');
      this.localStorage.set("appDirection", 'ltr');
    }

    const token = this.userService.getToken();
    const apiAccessToken = token.apiAccessSessionToken;

    if (!this.fgAppConstant["AppUtills"].isUndefined(apiAccessToken)) {
      const requestData = { filter: { token: apiAccessToken } };
      this.dataService.signOut(requestData).subscribe(() => {
        this.userService.logout();
        if (!directLogin && this.config?._postLogoutLandingPageId && this.config?._postLogoutLandingAppId) {
          const pageUrl = `page/${this.config._postLogoutLandingPageId}/${this.config._postLogoutLandingAppId}`;
          this.router.navigateByUrl(pageUrl);
        } else {
          this.goToLogin();
        }
      });
    }
  }

  openLogoutDialog(): void {
    const logoutDialog = this.dialog.open(DialogLogoutComponent, { width: '300px' });
    logoutDialog.afterClosed().subscribe(result => { if (result) this.logout(); });
  }

  goToLogin() {
    this.userService.logout();
    const session = this.userService.isUserLoggedIn();
    if (!session) this.router.navigate(["/app/login"], { queryParams: { logout: "true" } });
  }

  extractFirstLetters(loginId: string) {
    if (!loginId) return '';
    const words = loginId.split(" ");
    if (words.length >= 2) return words[0].charAt(0) + words[words.length - 1].charAt(0);
    return loginId.charAt(0);
  }
}
